/**
 * Guide API client
 *
 * getGuide()       — GET /api/guide → text/markdown
 * streamGuideChat() — POST /api/guide-chat (SSE stream)
 */

import { getAuthHeaders } from './http';

export async function getGuide(): Promise<string> {
  const headers = await getAuthHeaders();
  const res = await fetch('/api/guide', { headers });
  if (!res.ok) {
    throw new Error(`Failed to load guide: ${res.status}`);
  }
  return res.text();
}

export interface GuideChatContext {
  route: string;
  screen: string;
  database_id?: string;
}

export interface GuideChatMessage {
  role: string;
  content: string;
}

export interface GuideChatHandlers {
  onDelta: (text: string) => void;
  onDone: (usage?: unknown) => void;
  onError: (message: string) => void;
  signal: AbortSignal;
}

export async function streamGuideChat(
  body: {
    question: string;
    history: GuideChatMessage[];
    context: GuideChatContext;
  },
  handlers: GuideChatHandlers,
): Promise<void> {
  const authHeaders = await getAuthHeaders();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeaders,
  };

  let res: Response;
  try {
    res = await fetch('/api/guide/chat-stream', {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: handlers.signal,
    });
  } catch (err) {
    if ((err as { name?: string }).name === 'AbortError') return;
    handlers.onError('Network error — could not reach the server.');
    return;
  }

  if (!res.ok) {
    handlers.onError(`Server error: ${res.status}`);
    return;
  }

  const reader = res.body?.getReader();
  if (!reader) {
    handlers.onError('Response body is not readable.');
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';

  const parseLine = (line: string, eventType: string) => {
    if (!line.startsWith('data:')) return;
    const raw = line.slice(5).trim();
    try {
      const payload = JSON.parse(raw) as Record<string, unknown>;
      if (eventType === 'delta') {
        const text = typeof payload.text === 'string' ? payload.text : '';
        if (text) handlers.onDelta(text);
      } else if (eventType === 'done') {
        handlers.onDone(payload.usage);
      } else if (eventType === 'error') {
        const msg = typeof payload.message === 'string' ? payload.message : 'Unknown error';
        handlers.onError(msg);
      }
    } catch {
      // Malformed JSON in data line — ignore
    }
  };

  /**
   * SSE frame parser.
   * Frames are separated by a blank line (\n\n).
   * A frame may contain:
   *   event: <type>\n
   *   data: <json>\n
   *   \n
   */
  const processBuffer = () => {
    const frames = buffer.split('\n\n');
    // The last element is an incomplete (or empty) frame — keep it in the buffer
    buffer = frames.pop() ?? '';

    for (const frame of frames) {
      if (!frame.trim()) continue;
      let eventType = 'message';
      let dataLine = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) {
          eventType = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          dataLine = line;
        }
      }
      if (dataLine) {
        parseLine(dataLine, eventType);
      }
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      processBuffer();
    }
    // Flush any remaining buffer content
    buffer += decoder.decode();
    processBuffer();
  } catch (err) {
    if ((err as { name?: string }).name === 'AbortError') return;
    handlers.onError('Stream interrupted unexpectedly.');
  } finally {
    reader.releaseLock();
  }
}
